import React from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import QText from './QText';
import { getBatchTimeDisplay, showTimeZone, showDate } from '../utils';

const BatchInfoDisplay = ({
  queueUserDetail,
  showUserInfo = false,
  centerAlign = false,
  customStyles = {},
  showDateTimeOnly = false,
  showStartEndLabel = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = createStyles(theme);

  if (!queueUserDetail || queueUserDetail.queue_type !== 'Batch') {
    return null;
  }

  // Calculate booking times
  const now = moment();
  let bookingStart;

  if (queueUserDetail.queue_timezone) {
    // Strip the 'Z' part and treat as local time
    const datetimeString = moment(queueUserDetail.booking_datetime).format(
      'YYYY-MM-DDTHH:mm:ss[Z]'
    );
    bookingStart = moment(datetimeString.replace('Z', ''));
  } else {
    bookingStart = moment(queueUserDetail.booking_datetime);
  }

  const bookingEnd = bookingStart
    .clone()
    .add(queueUserDetail.serving_time, 'minutes');

  const isStarted = now.isSameOrAfter(bookingStart);
  const minutesUntilStart = bookingStart.diff(now, 'minutes');
  const minutesUntilEnd = bookingEnd.diff(now, 'minutes');

  const textStyle = [
    styles.batchInfoText,
    centerAlign && styles.centerText,
    customStyles.text,
  ];

  if (showDateTimeOnly) {
    return (
      <View style={[styles.container, customStyles.container]}>
        {/* Date */}
        <View style={styles.infoRow}>
          <QText style={textStyle}>{showDate(bookingStart)}</QText>
        </View>

        {/* Batch Start - End Label */}
        {showStartEndLabel && (
          <View style={styles.infoRow}>
            <QText style={textStyle}>
              {`${t('batch_start')} - ${t('batch_end')}`}
            </QText>
          </View>
        )}

        {/* Time Range */}
        <View style={styles.infoRow}>
          <QText style={textStyle}>
            {`${getBatchTimeDisplay(bookingStart, queueUserDetail.queue_timezone)} - ${getBatchTimeDisplay(bookingEnd, queueUserDetail.queue_timezone)}`}
            {showTimeZone(queueUserDetail.queue_timezone)
              ? ' ' + showTimeZone(queueUserDetail.queue_timezone)
              : ''}
          </QText>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, customStyles.container]}>
      {/* Status */}
      <View style={styles.infoRow}>
        <QText style={textStyle}>{queueUserDetail.status}</QText>
      </View>

      {/* User info (for admin view) */}
      {showUserInfo && queueUserDetail.name && (
        <View style={styles.infoRow}>
          <QText style={textStyle}>
            {t('name')}: {queueUserDetail.name}
          </QText>
        </View>
      )}

      {/* Date */}
      <View style={styles.infoRow}>
        <QText style={textStyle}>{showDate(bookingStart)}</QText>
      </View>

      {/* Batch Start - End Label */}
      {showStartEndLabel && (
        <View style={styles.infoRow}>
          <QText style={textStyle}>
            {`${t('batch_start')} - ${t('batch_end')}`}
          </QText>
        </View>
      )}

      {/* Time Range */}
      <View style={styles.infoRow}>
        <QText style={textStyle}>
          {`${getBatchTimeDisplay(bookingStart, queueUserDetail.queue_timezone)} - ${getBatchTimeDisplay(bookingEnd, queueUserDetail.queue_timezone)}`}
          {showTimeZone(queueUserDetail.queue_timezone)
            ? ' ' + showTimeZone(queueUserDetail.queue_timezone)
            : ''}
        </QText>
      </View>

      {/* Batch Status Message */}
      {queueUserDetail.status !== 'Removed' &&
        queueUserDetail.status !== 'Exited' && (
          <View style={[styles.statusContainer, customStyles.statusContainer]}>
            {isStarted ? (
              minutesUntilEnd > 0 ? (
                <QText style={[textStyle, styles.statusText]}>
                  Ending in {minutesUntilEnd} mins
                </QText>
              ) : (
                <QText style={[textStyle, styles.statusText]}>
                  Batch has ended
                </QText>
              )
            ) : minutesUntilStart > 0 ? (
              <QText style={[textStyle, styles.statusText]}>
                {minutesUntilStart >= 1440
                  ? `Starting in ${Math.floor(minutesUntilStart / 1440)} days ${Math.floor((minutesUntilStart % 1440) / 60)} hours ${minutesUntilStart % 60} mins`
                  : minutesUntilStart >= 60
                    ? `Starting in ${Math.floor(minutesUntilStart / 60)} hours ${minutesUntilStart % 60} mins`
                    : `Starting in ${minutesUntilStart} mins`}
              </QText>
            ) : (
              <QText style={[textStyle, styles.statusText]}>
                Starting soon
              </QText>
            )}
          </View>
        )}
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
    },
    infoRow: {
      marginVertical: 4,
    },
    batchInfoText: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.whiteTxt,
    },
    centerText: {
      textAlign: 'center',
    },
    statusContainer: {
      marginTop: 20,
    },
    statusText: {
      fontSize: 14,
    },
  });

export default BatchInfoDisplay;
